import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { VIEWS, COLLECTION_SCHEMAS, SINGLETON_SCHEMAS } from './schemas';
import { FieldInput, Modal } from './fields';
import BulkImport from './BulkImport';
import {
  LogOut, Plus, Pencil, Trash2, RefreshCw, Check, X, ExternalLink, Newspaper,
} from 'lucide-react';

const flat = VIEWS.flatMap((g) => g.items);

/* ---------------- Login ---------------- */
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const res = await api.login(email, password);
      localStorage.setItem('enersider_token', res.token);
      onLogin(res.user);
    } catch (e2) { setErr('Invalid email or password'); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#111111] p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl">
        <div className="font-logo text-2xl mb-1">ENER<span className="text-[#D01919]">S</span>IDER<span className="text-neutral-400 text-[0.5em] align-super">.com</span></div>
        <p className="text-sm text-neutral-500 mb-6">Content admin</p>
        <label className="text-sm font-medium">Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 mb-4 w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#D01919]" placeholder="admin@enersider.com" />
        <label className="text-sm font-medium">Password</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 mb-4 w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#D01919]" placeholder="••••••••" />
        {err ? <p className="text-sm text-[#D01919] mb-3">{err}</p> : null}
        <button disabled={busy} className="w-full bg-[#111111] text-white rounded-lg py-2.5 font-medium hover:bg-[#D01919] transition-colors disabled:opacity-50">{busy ? 'Signing in…' : 'Sign in'}</button>
      </form>
    </div>
  );
}

/* ---------------- Collection view ---------------- */
function CollectionView({ name }) {
  const schema = COLLECTION_SCHEMAS[name];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => setItems(await api.listCollection(name)), [name]);
  useEffect(() => { load(); }, [load]);
  const openNew = () => setEditing({});
  const save = async () => {
    setSaving(true);
    try {
      if (editing.id) await api.updateItem(name, editing.id, editing);
      else await api.createItem(name, editing);
      setEditing(null); await load();
    } finally { setSaving(false); }
  };
  const del = async (id) => { if (window.confirm('Delete this item?')) { await api.deleteItem(name, id); load(); } };
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold">{flat.find((v) => v.name === name)?.label || name}</h2>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 bg-[#111111] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#D01919] transition-colors"><Plus size={16} /> Add</button>
      </div>
      {name === 'the100Leaders' && <BulkImport name={name} onDone={load} />}
      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
        {items.length === 0 ? <p className="p-5 text-sm text-neutral-500">No items yet.</p> : items.map((it) => (
          <div key={it.id} className="flex items-center gap-4 p-4">
            {(it.image || it.cover) ? <img src={it.image || it.cover} alt="" className="w-12 h-12 rounded-lg object-cover" /> : <div className="w-12 h-12 rounded-lg bg-neutral-100" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{it[schema.titleKey] || '(untitled)'}</p>
              <p className="text-sm text-neutral-500 truncate">{it[schema.subKey]}</p>
            </div>
            <button onClick={() => setEditing(it)} className="p-2 text-neutral-500 hover:text-[#111111]"><Pencil size={16} /></button>
            <button onClick={() => del(it.id)} className="p-2 text-neutral-500 hover:text-[#D01919]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <Modal open={!!editing} onClose={() => setEditing(null)} onSave={save} saving={saving} title={editing?.id ? 'Edit item' : 'New item'}>
        {schema.fields.map((f) => (
          <FieldInput key={f.key} field={f} value={editing?.[f.key]}
            onChange={(v) => setEditing({ ...editing, [f.key]: v })}
            onCover={(url) => setEditing((cur) => ({ ...cur, cover: cur?.cover || url }))} />
        ))}
      </Modal>
    </div>
  );
}

/* ---------------- Singleton view ---------------- */
function SingletonView({ name }) {
  const schema = SINGLETON_SCHEMAS[name];
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.getSingleton(name).then((d) => setDoc(d || {})); }, [name]);
  const save = async () => { setSaving(true); try { await api.putSingleton(name, doc); setSaved(true); setTimeout(() => setSaved(false), 2500); } finally { setSaving(false); } };
  if (!doc) return <p className="text-sm text-neutral-500">Loading…</p>;
  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold mb-5">{flat.find((v) => v.name === name)?.label || name}</h2>
      <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4">
        {schema.map((f) => <FieldInput key={f.key} field={f} value={doc[f.key]} onChange={(v) => setDoc({ ...doc, [f.key]: v })} />)}
        <div className="flex items-center gap-3 pt-2">
          <button onClick={save} disabled={saving} className="bg-[#111111] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#D01919] transition-colors disabled:opacity-50">{saving ? 'Saving…' : 'Save changes'}</button>
          {saved ? <span className="text-sm text-green-600 inline-flex items-center gap-1"><Check size={15} /> Saved</span> : null}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Wire moderation ---------------- */
function SectorTags({ it }) {
  const tags = (it.sectors && it.sectors.length) ? it.sectors : (it.sector ? [it.sector] : []);
  if (!tags.length) return <span className="text-[0.6rem] font-semibold uppercase tracking-wider text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Needs review</span>;
  return <span className="flex flex-wrap gap-1">{tags.map((t) => <span key={t} className="text-[0.6rem] font-semibold text-neutral-600 bg-neutral-100 rounded px-1.5 py-0.5">{t}</span>)}</span>;
}

function WireView() {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [sectorFilter, setSectorFilter] = useState('');
  const [sectors, setSectors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (tab === 'review') {
      const pend = await api.listWire('pending');
      setItems(pend.filter((i) => i.needs_review || !((i.sectors && i.sectors.length) || i.sector)));
    } else {
      setItems(await api.listWire(tab));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.getSectors().then(setSectors).catch(() => setSectors([])); }, []);

  const fetchNews = async () => { setBusy(true); try { await api.fetchWire(); await load(); } finally { setBusy(false); } };
  const reclassify = async () => { setBusy(true); try { await api.reclassifyWire(); await load(); } finally { setBusy(false); } };
  const act = async (id, status) => { await api.updateWire(id, { status }); load(); };
  const del = async (id) => { if (window.confirm('Delete this story?')) { await api.deleteWire(id); load(); } };
  const saveEdit = async () => {
    setSaving(true);
    try { await api.updateWire(editing.id, { headline: editing.headline, url: editing.url, source: editing.source, sectors: editing.sectors || [] }); setEditing(null); await load(); }
    finally { setSaving(false); }
  };
  const toggleSector = (s) => setEditing((e) => {
    const cur = e.sectors || [];
    return { ...e, sectors: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
  });

  const shown = sectorFilter ? items.filter((i) => ((i.sectors && i.sectors.includes(sectorFilter)) || i.sector === sectorFilter)) : items;
  const tabs = [['pending', 'Pending'], ['review', 'Needs review'], ['approved', 'Live on site'], ['rejected', 'Rejected']];

  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">The Wire — Sector Desk</h2>
          <p className="text-sm text-neutral-500">Energy news from ANI · PTI · PIB, auto-classified into sectors. Approve items to publish them.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reclassify} disabled={busy} data-testid="wire-reclassify" className="inline-flex items-center gap-1.5 border border-neutral-300 px-3 py-2 rounded-lg text-sm font-medium hover:bg-neutral-100 disabled:opacity-50">Re-classify</button>
          <button onClick={fetchNews} disabled={busy} data-testid="wire-fetch" className="inline-flex items-center gap-1.5 bg-[#111111] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#D01919] transition-colors disabled:opacity-50"><RefreshCw size={16} className={busy ? 'animate-spin' : ''} /> Fetch latest</button>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex gap-1 bg-neutral-100 rounded-lg p-1">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`wire-tab-${k}`} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === k ? 'bg-white shadow-sm text-[#D01919]' : 'text-neutral-500 hover:text-neutral-800'}`}>{l}</button>
          ))}
        </div>
        <select value={sectorFilter} onChange={(e) => setSectorFilter(e.target.value)} data-testid="wire-sector-filter" className="border border-neutral-300 rounded-lg px-3 py-2 text-sm">
          <option value="">All sectors</option>
          {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
        {shown.length === 0 ? <p className="p-5 text-sm text-neutral-500">Nothing here. {tab === 'pending' ? 'Click "Fetch latest" to pull news.' : ''}</p> : shown.map((it) => (
          <div key={it.id} className="flex items-start gap-3 p-4" data-testid="wire-row">
            <span className="text-[0.6rem] font-bold uppercase tracking-wider text-[#D01919] border border-[#D01919]/40 rounded px-1.5 py-0.5 mt-0.5 shrink-0">{it.source}</span>
            <div className="flex-1 min-w-0">
              <a href={it.url} target="_blank" rel="noopener noreferrer" className="font-medium hover:text-[#D01919] inline-flex items-center gap-1">{it.headline} <ExternalLink size={13} /></a>
              <div className="mt-1.5"><SectorTags it={it} /></div>
            </div>
            <div className="flex gap-1 shrink-0">
              {tab !== 'approved' && <button onClick={() => act(it.id, 'approved')} data-testid="wire-approve" className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Approve"><Check size={17} /></button>}
              {tab !== 'rejected' && <button onClick={() => act(it.id, 'rejected')} data-testid="wire-reject" className="p-2 text-neutral-400 hover:text-amber-600 rounded-lg" title="Reject"><X size={17} /></button>}
              <button onClick={() => setEditing({ ...it, sectors: (it.sectors && it.sectors.length) ? it.sectors : (it.sector ? [it.sector] : []) })} data-testid="wire-edit" className="p-2 text-neutral-500 hover:text-[#111111] rounded-lg" title="Edit / retag"><Pencil size={16} /></button>
              <button onClick={() => del(it.id)} data-testid="wire-delete" className="p-2 text-neutral-400 hover:text-[#D01919] rounded-lg" title="Delete"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} onSave={saveEdit} saving={saving} title="Edit / retag story">
        {editing && (
          <>
            <div><label className="text-sm font-medium">Headline</label>
              <input value={editing.headline || ''} onChange={(e) => setEditing({ ...editing, headline: e.target.value })} className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-sm font-medium">Source</label>
              <input value={editing.source || ''} onChange={(e) => setEditing({ ...editing, source: e.target.value })} className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-sm font-medium">Link (URL)</label>
              <input value={editing.url || ''} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className="mt-1 w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="text-sm font-medium">Sectors (tag one or more)</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {sectors.map((s) => {
                  const on = (editing.sectors || []).includes(s);
                  return <button type="button" key={s} onClick={() => toggleSector(s)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? 'bg-[#111111] text-white border-[#111111]' : 'bg-white text-neutral-600 border-neutral-300 hover:border-neutral-500'}`}>{s}</button>;
                })}
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

/* ---------------- Sector classification rules ---------------- */
function SectorRulesView() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.getSectorRules().then(setRules).catch(() => setRules([])); }, []);
  const setKw = (i, text) => setRules((r) => r.map((row, idx) => idx === i ? { ...row, keywords: text.split(',').map((x) => x.trim()).filter(Boolean) } : row));
  const addSector = () => setRules((r) => [...r, { sector: '', keywords: [] }]);
  const setName = (i, v) => setRules((r) => r.map((row, idx) => idx === i ? { ...row, sector: v } : row));
  const remove = (i) => setRules((r) => r.filter((_, idx) => idx !== i));
  const save = async () => { setSaving(true); try { await api.putSectorRules(rules); setSaved(true); setTimeout(() => setSaved(false), 2500); } finally { setSaving(false); } };
  if (!rules) return <p className="text-sm text-neutral-500">Loading…</p>;
  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-semibold mb-1">Sector Classification Rules</h2>
      <p className="text-sm text-neutral-500 mb-5">Edit the keywords used to auto-tag incoming news into each sector. Comma-separated. A story that matches no keyword goes to the "Needs review" queue.</p>
      <div className="space-y-3">
        {rules.map((r, i) => (
          <div key={i} className="bg-white rounded-xl border border-neutral-200 p-4" data-testid="sector-rule-row">
            <div className="flex items-center gap-2 mb-2">
              <input value={r.sector} onChange={(e) => setName(i, e.target.value)} placeholder="Sector name" className="font-medium border border-neutral-300 rounded-lg px-3 py-1.5 text-sm flex-1" />
              <button onClick={() => remove(i)} className="text-neutral-400 hover:text-[#D01919] p-1"><Trash2 size={16} /></button>
            </div>
            <textarea value={(r.keywords || []).join(', ')} onChange={(e) => setKw(i, e.target.value)} rows={2} placeholder="keyword1, keyword2, …" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-y" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={addSector} className="inline-flex items-center gap-1 text-sm text-[#D01919] font-medium"><Plus size={15} /> Add sector</button>
        <div className="flex-1" />
        <button onClick={save} disabled={saving} data-testid="sector-rules-save" className="bg-[#111111] text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-[#D01919] transition-colors disabled:opacity-50">{saving ? 'Saving…' : 'Save rules'}</button>
        {saved ? <span className="text-sm text-green-600 inline-flex items-center gap-1"><Check size={15} /> Saved</span> : null}
      </div>
    </div>
  );
}

/* ---------------- Users ---------------- */
function UsersView() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: '', password: '', name: '' });
  const [err, setErr] = useState('');
  const load = async () => setUsers(await api.listUsers());
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); setErr(''); try { await api.createUser(form); setForm({ email: '', password: '', name: '' }); load(); } catch (e2) { setErr(e2?.response?.data?.detail || 'Error'); } };
  const del = async (id) => { if (window.confirm('Remove this admin?')) { try { await api.deleteUser(id); load(); } catch (e2) { alert(e2?.response?.data?.detail || 'Error'); } } };
  return (
    <div className="max-w-xl">
      <h2 className="text-xl font-semibold mb-5">Admin Users</h2>
      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100 mb-6">
        {users.map((u) => (
          <div key={u.id} className="flex items-center gap-3 p-4">
            <div className="flex-1"><p className="font-medium">{u.name}</p><p className="text-sm text-neutral-500">{u.email}</p></div>
            <button onClick={() => del(u.id)} className="p-2 text-neutral-400 hover:text-[#D01919]"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="bg-white rounded-xl border border-neutral-200 p-5 space-y-3">
        <h3 className="font-medium">Add an admin</h3>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Password" className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm" />
        {err ? <p className="text-sm text-[#D01919]">{err}</p> : null}
        <button className="bg-[#111111] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#D01919] transition-colors">Add admin</button>
      </form>
    </div>
  );
}

/* ---------------- Leads ---------------- */
function LeadsView() {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.listLeads().then(setLeads).catch(() => setLeads([])); }, []);
  return (
    <div>
      <h2 className="text-xl font-semibold mb-5">Form Submissions</h2>
      <div className="bg-white rounded-xl border border-neutral-200 divide-y divide-neutral-100">
        {leads.length === 0 ? <p className="p-4 text-sm text-neutral-500">No submissions yet.</p> : leads.map((l) => (
          <div key={l.id} className="p-4 text-sm">
            <div className="flex justify-between"><span className="font-medium">{l.type || 'Enquiry'}</span><span className="text-neutral-400">{(l.created_at || '').slice(0, 10)}</span></div>
            <pre className="text-neutral-600 whitespace-pre-wrap mt-1 font-sans">{JSON.stringify({ ...l, id: undefined, created_at: undefined, _id: undefined }, null, 1)}</pre>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Dashboard ---------------- */
function Dashboard({ go }) {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-2">Welcome back</h2>
      <p className="text-sm text-neutral-500 mb-6">Edit any part of the site from the menu. Changes go live immediately.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {[['wire', 'Approve news', Newspaper], ['editions', 'Add an edition', Plus], ['coverStory', 'Update cover story', Pencil]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => go(k)} className="bg-white border border-neutral-200 rounded-xl p-5 text-left hover:border-[#D01919] transition-colors">
            <Icon size={20} className="text-[#D01919]" />
            <p className="font-medium mt-3">{label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Shell ---------------- */
export default function AdminApp() {
  const [user, setUser] = useState(undefined); // undefined=loading, null=logged out
  const [active, setActive] = useState('dashboard');
  useEffect(() => {
    const token = localStorage.getItem('enersider_token');
    if (!token) { setUser(null); return; }
    api.me().then(setUser).catch(() => { localStorage.removeItem('enersider_token'); setUser(null); });
  }, []);
  const logout = () => { localStorage.removeItem('enersider_token'); setUser(null); };
  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Loading…</div>;
  if (!user) return <Login onLogin={setUser} />;
  const view = flat.find((v) => v.key === active) || flat[0];
  return (
    <div className="min-h-screen flex bg-neutral-50">
      <aside className="w-64 bg-[#111111] text-white flex flex-col shrink-0 h-screen sticky top-0">
        <div className="p-5 border-b border-white/10">
          <div className="font-logo text-xl">ENER<span className="text-[#D01919]">S</span>IDER</div>
          <p className="text-xs text-white/40 mt-1">Content Admin</p>
        </div>
        <nav className="flex-1 overflow-auto py-3">
          {VIEWS.map((g) => (
            <div key={g.group} className="mb-3">
              <p className="px-5 py-1 text-[0.62rem] uppercase tracking-widest text-white/30">{g.group}</p>
              {g.items.map((it) => (
                <button key={it.key} onClick={() => setActive(it.key)} className={`w-full text-left px-5 py-2 text-sm transition-colors ${active === it.key ? 'bg-[#D01919] text-white' : 'text-white/70 hover:bg-white/5'}`}>{it.label}</button>
              ))}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 flex items-center justify-between">
          <Link to="/" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"><ExternalLink size={13} /> View site</Link>
          <button onClick={logout} className="text-xs text-white/60 hover:text-[#D01919] inline-flex items-center gap-1"><LogOut size={13} /> Sign out</button>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10 overflow-auto">
        {view.type === 'dashboard' && <Dashboard go={setActive} />}
        {view.type === 'wire' && <WireView />}
        {view.type === 'sectorRules' && <SectorRulesView />}
        {view.type === 'collection' && <CollectionView name={view.name} key={view.name} />}
        {view.type === 'singleton' && <SingletonView name={view.name} key={view.name} />}
        {view.type === 'users' && <UsersView />}
        {view.type === 'leads' && <LeadsView />}
      </main>
    </div>
  );
}
